// W12 step A5 (2026-10-09): quil's adapter onto the shared account-scope contract
// (app-platform/design/W12-storage.md section 3.1 and 3.2, rollout row A5; the canonical rules live
// in `storageScope.ts`, `migrations.ts`, `idbBackup.ts` and `firstBoot.ts`, which this file only
// feeds and obeys). It follows bindr's adoption (`5d74c6f`, `bindrScope.ts`), inkling's (`782e781`)
// and contently's (`21229c7`), and differs where quil does.
//
// What was wrong. The local library was ONE fixed Dexie database, `QuilDatabase`, and the only
// thing that said whose it was, `settings.oneDriveUserId`, was REWRITTEN on every sign-in
// (AuthProvider) and only ever read as a "trusted device" boolean. Sign a second Microsoft account
// into the same browser and it inherited the first account's `syncQueue`, drained it with its own
// token into its own AppFolder, and stamped the store with itself. This module is the fix.
//
// - **Scoped databases.** An account's store is `QuilDatabase@<homeAccountId>` (`scopedStoreName`).
// - **Adoption in place, with proof, never a guess.** The pre-W12 `QuilDatabase` keeps its name and
//   is adopted by an owner stamp in its `meta` table (the v12 store). `decideStore` runs the ladder
//   with Kamrin's settled policy (2026-10-09):
//     P1  the stamp, or, for a store still at v11, `settings.oneDriveUserId`. This is quil's own
//         proof and the most certain adoption in the estate: every pre-W12 sign-in wrote it. The v12
//         Dexie upgrade turns it into a real stamp IN THE SAME TRANSACTION as the version change
//         (db.ts), so the stamp and the schema commit together or not at all.
//     P2  session continuity: signed in at the first boot of this build, with no interactive sign-in
//         since (`firstBoot.ts`, recorded in `auth.ts` between MSAL initialize() and
//         handleRedirectPromise()). MSAL's account list is never the proof.
//     P3  remote overlap, by id: of the legacy store's SYNCED works (no queued upload, no
//         needsUpload chapter, and an eTag or remote tag on file) at least half are folders in THIS
//         account's `/Apps/Quil/works`. A non-empty listing with no overlap means the store is
//         someone else's. A failed or slow listing is "unknown", never a proof, never an adopt.
//     P4  nothing provable: HOLD. The account gets a fresh scoped store, the legacy database is not
//         opened, not synced and not deleted, and Settings asks (These are mine / Export / Delete).
// - **Until the store is decided nothing reads or syncs the legacy store.** The only thing that looks
//   at it before then is `readStoreRaw`, a raw IndexedDB probe that opens it without upgrading and
//   reads only what the decision needs (ids and flags, never prose into the result).
// - **No stopgap store.** Contently's cache can fall back to a throwaway store when the ladder
//   fails, and learned in its 21229c7 that such a fallback must never mark the scoped store as
//   existing, or the owner's own legacy library is HELD on the next boot. Quil's store holds the
//   only copy of unsynced prose, so it falls back to nothing: any failure rejects with the cause,
//   the gate shows a retry, no store is opened, nothing is stamped and no marker is written. The next
//   boot runs the ladder afresh, and a failed boot followed by a working one ADOPTS.
// - **The migration.** v12 is additive, so a FULLY SYNCED store needs no backup (the remote is the
//   copy). With unsynced work (queued uploads, `needsUpload` chapters, open conflicts) the shared
//   runner copies the store first, and if it cannot (quota) the store stays at its old version and
//   opens READ-ONLY (decision 3). An older chain (v4 to v5 scenes, v6 to v7 status remap, v7 to v8
//   word counts, v9 to v10 conflicts copy, v10 to v11 drop) reshapes, so it always gets the backup
//   and the dry run, synced or not. A store from a newer build raises `NewerDataError` and is
//   opened read-only through a dynamic Dexie view that refuses every write at the DBCore layer.
//
// The held-data actions (claim, export, delete) are in `quilHeld.ts`, reached only from Settings.

import Dexie from 'dexie'
import { createIdbMigrationIo, databaseExists, openExisting } from './idbBackup'
import { isNewerDataError, runMigration } from './migrations'
import type { MigrationPolicy, MigrationReport, MigrationStep } from './migrations'
import { firstBootEvidence } from './firstBoot'
import {
  decideStore,
  ownerStamp,
  parseOwnerStamp,
  scopeFromDecision,
  scopedStoreName,
  ScopeMismatchError,
} from './storageScope'
import type { LegacyStoreState, OwnerStamp, OwnershipEvidence, ScopePolicy, StoreDecision, StoreScope } from './storageScope'
import { QuilDatabase, SCHEMA_VERSION, putOwner, readOwner } from './db'
import { BASE, FIRST_BOOT_KEY, markerKey } from './scopeKeys'
import type { ReadOnlyInfo } from './readOnly'

export type { ReadOnlyInfo }

/** Kamrin's answers to W12 decisions 1 and 2 (2026-10-09), stated rather than left implicit.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export const QUIL_SCOPE_POLICY: Partial<ScopePolicy> = { unprovableOwner: 'hold-and-ask', sessionContinuity: 'first-boot-signed-in' }
/** Decision 3: a migration that cannot write its backup while unsynced work exists does not run.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export const QUIL_MIGRATION_POLICY: Partial<MigrationPolicy> = { backupFailureWithUnsynced: 'stay-read-only' }

/**
 * The chain, as the runner plans it (Dexie versions 1 to 13; IndexedDB keeps them x10). A step that
 * rewrites existing rows RESHAPES (always backed up and dry-run, synced or not); the others add or
 * drop a table or index and move no row. v12 is the only step this file's adoption adds.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export const MIGRATION_STEPS: readonly MigrationStep[] = [
  { from: 1, to: 2, name: 'v2: workId index on syncQueue', reshapes: false },
  { from: 2, to: 3, name: 'v3: prose conflicts store', reshapes: false },
  { from: 3, to: 4, name: 'v4: tombstones store', reshapes: false },
  { from: 4, to: 5, name: 'v5: scenes, a default scene per chapter', reshapes: true },
  { from: 5, to: 6, name: 'v6: drop the unused marks store', reshapes: false, shrinks: ['marks'] },
  { from: 6, to: 7, name: 'v7: chapter status remap', reshapes: true },
  { from: 7, to: 8, name: 'v8: lazy prose, word counts and proseLoaded', reshapes: true },
  { from: 8, to: 9, name: 'v9: snapshots, trash and cuts stores', reshapes: false },
  { from: 9, to: 10, name: 'v10: syncConflicts and remoteTags, conflicts copied across', reshapes: true },
  { from: 10, to: 11, name: 'v11: drop the old conflicts store', reshapes: true, shrinks: ['conflicts'] },
  { from: 11, to: 12, name: 'v12: meta store, the owner stamp (W12)', reshapes: false },
  { from: 12, to: 13, name: 'v13: drop the unused sessions store and the fields of cut features', reshapes: true, shrinks: ['sessions'] },
]

/** How long P3 waits for OneDrive's listing before calling the evidence unknown. */
const LISTING_TIMEOUT_MS = 8000

export interface ResolvedStore {
  scope: StoreScope
  decision: StoreDecision
  /** Pre-W12 data of unprovable ownership is being held (Settings shows the held-data section). */
  held: boolean
  db: QuilDatabase
  /** Non-null when the store must not be written (every local write and every OneDrive request is refused). */
  readOnly: ReadOnlyInfo | null
  migration: MigrationReport | null
}

/** Resolving the store failed. Nothing was adopted, stamped or marked; the next boot tries afresh.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export class StoreUnavailableError extends Error {
  readonly causeError: unknown
  constructor(causeError: unknown) {
    super(`Quil could not open this device's library: ${causeError instanceof Error ? causeError.message : String(causeError)}`)
    this.causeError = causeError
    this.name = 'StoreUnavailableError'
  }
}

/** A write reached a store that is open read-only. Nothing was changed. */
export class ReadOnlyError extends Error {
  constructor(message = 'This device’s copy of your library is open read-only, so nothing was changed.') {
    super(message)
    this.name = 'ReadOnlyError'
  }
}

export interface ResolveDeps {
  factory?: IDBFactory
  /** Where the "this account has its own store" markers live (localStorage). */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  /** P2's recorded evidence. */
  firstBoot?: () => OwnershipEvidence['signedInAtFirstBoot']
  /** The ids of this account's works on OneDrive (folder names), for P3. Absent, or throwing, means "unknown". */
  listRemote?: () => Promise<ReadonlySet<string>>
  now?: () => string
  listTimeoutMs?: number
  /** Test seam: how a database is opened (a failing upgrade, a failing stamp put). */
  makeDb?: (name: string) => QuilDatabase
  /** Called whenever a newer-build store is met (feeds the W14 gate for the next boot). */
  onNewerData?: () => void
}

function factoryOf(deps: ResolveDeps): IDBFactory {
  if (deps.factory) return deps.factory
  if (typeof indexedDB === 'undefined') throw new Error('IndexedDB is unavailable')
  return indexedDB
}

function makeDbFor(deps: ResolveDeps, name: string): QuilDatabase {
  if (deps.makeDb) return deps.makeDb(name)
  return deps.factory ? new QuilDatabase(name, { indexedDB: deps.factory, IDBKeyRange: globalThis.IDBKeyRange }) : new QuilDatabase(name)
}

function storageOf(deps: ResolveDeps): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  if (deps.storage !== undefined) return deps.storage
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

// ------------------------------------------------------------------------------ the raw probe

interface RawChapter {
  id: string
  workId: string
  /** The durable crash-flush marker: this body still needs uploading. */
  needsUpload: boolean
  /** A stored eTag: this chapter's prose was synced at some point. */
  tagged: boolean
}

export interface RawRows {
  /** The raw IndexedDB version (Dexie keeps `version * 10`). */
  idbVersion: number
  workIds: string[]
  chapters: RawChapter[]
  /** One per queued upload or delete, with the work it belongs to. */
  queue: Array<{ workId: string }>
  /** `syncConflicts` rows not yet resolved. */
  openConflicts: number
  /** Paths of `remoteTags` (the last-seen eTag of a meta or aggregate file): proof a work was synced. */
  remotePaths: string[]
  trash: number
  snapshots: number
  /** The v12 stamp, else `settings.oneDriveUserId` (P1 for a store not yet at v12), else null. */
  owner: OwnerStamp | null
}

function requestResult<T>(r: IDBRequest<T>, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error ?? new Error(`could not read ${what}`))
  })
}

/** Visit every row of a store through a cursor, so no store is ever held in memory whole. */
function eachRow(idb: IDBDatabase, store: string, visit: (value: Record<string, unknown>) => void): Promise<void> {
  if (!idb.objectStoreNames.contains(store)) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const cursor = idb.transaction(store, 'readonly').objectStore(store).openCursor()
    cursor.onsuccess = () => {
      const c = cursor.result
      if (!c) return resolve()
      const v: unknown = c.value
      if (v && typeof v === 'object') visit(v as Record<string, unknown>)
      c.continue()
    }
    cursor.onerror = () => reject(cursor.error ?? new Error(`could not read ${store}`))
  })
}

function countOf(idb: IDBDatabase, store: string): Promise<number> {
  if (!idb.objectStoreNames.contains(store)) return Promise.resolve(0)
  return requestResult(idb.transaction(store, 'readonly').objectStore(store).count(), `${store} count`)
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/**
 * Read a store WITHOUT upgrading it (a raw `open(name)` with no version) and WITHOUT Dexie, so it
 * works on a v1 store through a v12 store and a store from a newer build alike. Never creates:
 * `null` when no such database exists. Chapter prose is never copied into the result: each row is
 * read by a cursor and only its flags are kept.
 */
export async function readStoreRaw(factory: IDBFactory, name: string): Promise<RawRows | null> {
  const idb = await openExisting(factory, name)
  if (!idb) return null
  try {
    const workIds: string[] = []
    await eachRow(idb, 'works', (w) => { if (typeof w.id === 'string') workIds.push(w.id) })
    const chapters: RawChapter[] = []
    await eachRow(idb, 'chapters', (c) => {
      if (typeof c.id !== 'string') return
      chapters.push({ id: c.id, workId: str(c.workId), needsUpload: c.needsUpload === true, tagged: typeof c.cTag === 'string' && c.cTag.length > 0 })
    })
    const queue: Array<{ workId: string }> = []
    await eachRow(idb, 'syncQueue', (q) => { queue.push({ workId: str(q.workId) }) })
    let openConflicts = 0
    await eachRow(idb, 'syncConflicts', (r) => { if (!r.resolved) openConflicts += 1 })
    const remotePaths: string[] = []
    await eachRow(idb, 'remoteTags', (t) => { if (typeof t.path === 'string') remotePaths.push(t.path) })

    const meta = idb.objectStoreNames.contains('meta')
      ? await requestResult(idb.transaction('meta', 'readonly').objectStore('meta').get('owner'), 'the owner stamp')
      : null
    let owner = parseOwnerStamp(meta)
    if (!owner && idb.objectStoreNames.contains('settings')) {
      // P1 for a store not yet at v12 (or one never stamped): the id every pre-W12 sign-in wrote.
      const settings = await requestResult<unknown>(idb.transaction('settings', 'readonly').objectStore('settings').get(1), 'settings')
      const id = settings && typeof settings === 'object' ? (settings as Record<string, unknown>).oneDriveUserId : undefined
      if (typeof id === 'string' && id.length > 0) owner = ownerStamp(id, 'owner-stamp', '')
    }

    return {
      idbVersion: idb.version,
      workIds,
      chapters,
      queue,
      openConflicts,
      remotePaths,
      trash: await countOf(idb, 'trash'),
      snapshots: await countOf(idb, 'snapshots'),
      owner,
    }
  } finally {
    idb.close()
  }
}

/** The breakdown of work that exists nowhere else on this device's side of OneDrive. */
function unsyncedBreakdown(rows: RawRows) {
  return {
    queued: rows.queue.length,
    needsUpload: rows.chapters.filter((c) => c.needsUpload).length,
    openConflicts: rows.openConflicts,
  }
}

/** Work that exists nowhere else: queued uploads and deletes, `needsUpload` chapters, open conflicts. */
export function countUnsynced(rows: RawRows): number {
  const b = unsyncedBreakdown(rows)
  return b.queued + b.needsUpload + b.openConflicts
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function legacyStateOf(rows: RawRows): LegacyStoreState {
  return {
    owner: rows.owner?.accountId ?? null,
    hasData:
      rows.workIds.length > 0 ||
      rows.chapters.length > 0 ||
      rows.queue.length > 0 ||
      rows.openConflicts > 0 ||
      rows.trash > 0,
    unsynced: countUnsynced(rows),
  }
}

/**
 * P3, from the legacy rows and THIS account's OneDrive listing. `null` is "unknown", which
 * `decideStore` treats as no proof at all.
 *
 *   syncedLocal  works this device agreed with OneDrive on: nothing queued, no needsUpload chapter,
 *                and a stored eTag (a chapter's, or a meta or aggregate file's remote tag). A work
 *                that was never synced proves nothing about overlap.
 *   overlapping  of those, how many are folders in the listing
 *   eTagMatch    always false: only folder names are listed, not eTags
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function remoteEvidence(rows: RawRows, remoteWorkIds: ReadonlySet<string>): NonNullable<OwnershipEvidence['remote']> {
  const queued = new Set(rows.queue.map((q) => q.workId))
  const pending = new Set(rows.chapters.filter((c) => c.needsUpload).map((c) => c.workId))
  const tagged = new Set(rows.chapters.filter((c) => c.tagged).map((c) => c.workId))
  for (const path of rows.remotePaths) {
    const m = /\/works\/([^/]+)\//.exec(path)
    if (m?.[1]) tagged.add(m[1])
  }
  const synced = rows.workIds.filter((id) => !queued.has(id) && !pending.has(id) && tagged.has(id))
  return {
    remoteCount: remoteWorkIds.size,
    syncedLocal: synced.length,
    overlapping: synced.filter((id) => remoteWorkIds.has(id)).length,
    eTagMatch: false,
  }
}

// ------------------------------------------------------------------------------ the scoped marker

function scopedExists(deps: ResolveDeps, accountId: string): boolean {
  try {
    return storageOf(deps)?.getItem(markerKey(accountId)) != null
  } catch {
    return false
  }
}

/** Written ONLY after a real decision's store has opened (never from an error path). */
function writeMarker(deps: ResolveDeps, accountId: string): void {
  try {
    storageOf(deps)?.setItem(markerKey(accountId), (deps.now ?? (() => new Date().toISOString()))())
  } catch {
    /* unwritable: the next boot reads "no scoped store yet" and creates it again, harmlessly */
  }
}

export function removeMarker(deps: ResolveDeps, accountId: string): void {
  try {
    storageOf(deps)?.removeItem(markerKey(accountId))
  } catch {
    /* ignore */
  }
}

// ------------------------------------------------------------------------------ opening a store

/** Tables a v12 store has that an older store may not: stand-ins so a read-only view of it still reads. */
const emptyTable = {
  toArray: async () => [],
  get: async () => undefined,
  bulkGet: async (keys: unknown[]) => keys.map(() => undefined),
  count: async () => 0,
  put: () => Promise.reject(new ReadOnlyError()),
  add: () => Promise.reject(new ReadOnlyError()),
  delete: () => Promise.reject(new ReadOnlyError()),
}
const SCHEMA_TABLES = [
  'works', 'chapters', 'scenes', 'characters', 'pairings', 'plotPoints', 'canonNotes', 'syncQueue',
  'settings', 'syncConflicts', 'remoteTags', 'tombstones', 'snapshots', 'trash', 'cuts', 'meta',
] as const

/**
 * A read-only view of a store at WHATEVER version it has: opened dynamically (no schema declared, so
 * Dexie neither upgrades nor refuses a newer version), with a stand-in for any table that version
 * does not have yet, and a DBCore middleware that REFUSES EVERY READWRITE TRANSACTION. That last part
 * is the guarantee: it does not depend on each caller remembering not to write.
 */
async function openReadOnlyView(deps: ResolveDeps, name: string): Promise<QuilDatabase> {
  const factory = factoryOf(deps)
  const d = new Dexie(name, deps.factory ? { indexedDB: factory, IDBKeyRange: globalThis.IDBKeyRange } : undefined)
  d.use({
    stack: 'dbcore',
    name: 'quil-read-only',
    create: (down) => ({
      ...down,
      transaction: (stores, mode, options) => {
        if (mode === 'readwrite') throw new ReadOnlyError()
        return down.transaction(stores, mode, options)
      },
    }),
  })
  await d.open()
  // A database opened with no declared schema has its tables (`d.table(name)`) but not `d.works`:
  // put each table on the instance under its own name, as the declared class has them.
  for (const t of d.tables) Object.defineProperty(d, t.name, { value: t, configurable: true })
  for (const table of SCHEMA_TABLES) {
    if (!d.tables.some((t) => t.name === table)) Object.defineProperty(d, table, { value: emptyTable, configurable: true })
  }
  return d as unknown as QuilDatabase
}

export interface OpenedStore {
  db: QuilDatabase
  readOnly: ReadOnlyInfo | null
  migration: MigrationReport | null
}

const NEWER_REASON = 'This device’s copy of your library was saved by a newer version of Quil. Reload to update. Nothing will be changed until then.'
const HELD_REASON = 'Quil could not make a safe copy of your unsynced writing before updating this device’s storage, so it is open read-only. Sync or free some space, then reload.'
const FAILED_REASON = 'Quil could not update this device’s copy. Nothing was lost, and it is open read-only until it can.'

/**
 * Open `name` at this build's schema, running the shared migration runner first when the store
 * already exists (a new store is created at v12 directly). Never upgrades a store it cannot back up
 * while it holds unsynced work, and never writes a store from a newer build.
 */
export async function openStore(name: string, deps: ResolveDeps): Promise<OpenedStore> {
  const factory = factoryOf(deps)
  if (!(await databaseExists(factory, name))) {
    const db = makeDbFor(deps, name)
    await db.open()
    return { db, readOnly: null, migration: null }
  }
  const io = createIdbMigrationIo({
    factory,
    name,
    openAt: async (dbName) => {
      const d = makeDbFor(deps, dbName)
      await d.open()
      d.close()
    },
    unsynced: async (dbName) => {
      const rows = await readStoreRaw(factory, dbName)
      return rows ? countUnsynced(rows) : 0
    },
    fromIdbVersion: (v) => v / 10,
    ...(deps.now ? { now: deps.now } : {}),
  })
  let report: MigrationReport
  try {
    report = await runMigration(io, { target: SCHEMA_VERSION, steps: MIGRATION_STEPS, policy: QUIL_MIGRATION_POLICY })
  } catch (e) {
    if (isNewerDataError(e)) {
      deps.onNewerData?.()
      return { db: await openReadOnlyView(deps, name), readOnly: { kind: 'newer', reason: NEWER_REASON }, migration: null }
    }
    throw e
  }
  if (report.status === 'current' || report.status === 'migrated') {
    const db = makeDbFor(deps, name)
    await db.open()
    return { db, readOnly: null, migration: report }
  }
  const db = await openReadOnlyView(deps, name)
  if (report.status === 'held-read-only') return { db, readOnly: { kind: 'held', reason: HELD_REASON }, migration: report }
  return { db, readOnly: { kind: 'failed', reason: FAILED_REASON }, migration: report }
}

// ------------------------------------------------------------------------------ resolving

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  return Promise.race([
    p,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error('timed out')), ms)
    }),
  ]).finally(() => clearTimeout(timer))
}

/**
 * Decide which store this account opens and PERFORM the decision. A failure anywhere rejects with
 * `StoreUnavailableError`; it never adopts, never stamps, never marks a scoped store as existing,
 * and never leaves anything half-decided that a later boot could misread.
 */
export async function resolveStore(accountId: string, deps: ResolveDeps = {}): Promise<ResolvedStore> {
  try {
    return await resolveStoreUnsafe(accountId, deps)
  } catch (e) {
    throw new StoreUnavailableError(e)
  }
}

async function resolveStoreUnsafe(accountId: string, deps: ResolveDeps): Promise<ResolvedStore> {
  const factory = factoryOf(deps)
  const now = deps.now ?? (() => new Date().toISOString())
  const firstBoot = deps.firstBoot ? deps.firstBoot() : firstBootEvidence(FIRST_BOOT_KEY)

  const raw = await readStoreRaw(factory, BASE)
  const legacy = raw ? legacyStateOf(raw) : null
  const decide = (remote: OwnershipEvidence['remote'] | undefined): StoreDecision =>
    decideStore({
      base: BASE,
      accountId,
      scopedExists: scopedExists(deps, accountId),
      legacy,
      evidence: { signedInAtFirstBoot: firstBoot, ...(remote !== undefined ? { remote } : {}) },
      policy: QUIL_SCOPE_POLICY,
    })

  let decision = decide(undefined)
  // P3 only when P1 and P2 did not decide and there is unclaimed data to prove: it needs the network.
  if (decision.kind === 'hold' && raw && legacy && legacy.owner === null && legacy.hasData && deps.listRemote) {
    let listing: ReadonlySet<string> | null
    try {
      listing = await withTimeout(deps.listRemote(), deps.listTimeoutMs ?? LISTING_TIMEOUT_MS)
    } catch {
      listing = null // offline, signed out, a failed listing: unknown, so the hold stands
    }
    if (listing) decision = decide(remoteEvidence(raw, listing))
  }

  switch (decision.kind) {
    case 'adopt-legacy': {
      const opened = await openStore(BASE, deps)
      if (opened.readOnly) {
        // Cannot stamp a store that could not be upgraded: nothing is committed, the ladder re-runs.
        return { scope: scopeFromDecision(accountId, decision), decision, held: false, ...opened }
      }
      await putOwner(opened.db, ownerStamp(decision.stamp, decision.why, now())) // the commit point
      return { scope: scopeFromDecision(accountId, decision), decision, held: false, ...opened }
    }
    case 'open-legacy': {
      const opened = await openStore(BASE, deps)
      // The v12 upgrade stamps from `oneDriveUserId`; a store that is already v12 has its stamp.
      // Stamping a store whose owner is already this account (the decision says it is) restates a
      // fact, and makes the stamp the only thing the owner check ever needs to read.
      if (!opened.readOnly && !(await ownerStampOf(opened.db))) {
        await putOwner(opened.db, ownerStamp(accountId, 'owner-stamp', now()))
      }
      return { scope: scopeFromDecision(accountId, decision), decision, held: false, ...opened }
    }
    case 'new-scoped':
    case 'open-scoped':
    case 'hold': {
      const opened = await openStore(decision.name, deps)
      if (!opened.readOnly && !(await ownerStampOf(opened.db))) await putOwner(opened.db, ownerStamp(accountId, 'new-store', now()))
      // Only now, with the store open and stamped, is it a decision a later boot may rely on.
      if (!opened.readOnly) writeMarker(deps, accountId)
      return { scope: scopeFromDecision(accountId, decision), decision, held: decision.kind === 'hold', ...opened }
    }
  }
}

/** The `meta` stamp alone (not the `oneDriveUserId` fallback), or null. */
async function ownerStampOf(database: QuilDatabase): Promise<OwnerStamp | null> {
  const meta = (database as { meta?: { get(key: string): Promise<unknown> } }).meta
  return meta ? parseOwnerStamp(await meta.get('owner')) : null
}

/**
 * The account the legacy store is stamped for (or, pre-v12, `settings.oneDriveUserId`), or null.
 * Read without upgrading or creating anything. It lets a device that was already in use before W12
 * reopen its own library offline on its very first W12 boot, before any sign-in has left a pointer.
 */
export async function legacyOwner(deps: ResolveDeps = {}): Promise<string | null> {
  const raw = await readStoreRaw(factoryOf(deps), BASE)
  return raw?.owner?.accountId ?? null
}

/**
 * Which store an account ALREADY owns, without opening, creating or changing anything: its scoped
 * store if it has a marker and the database exists, else the legacy store if the stamp (or
 * `oneDriveUserId`) names it. Anything else is `null`.
 */
export async function findOwnedStore(accountId: string, deps: ResolveDeps = {}): Promise<'scoped' | 'legacy' | null> {
  const factory = factoryOf(deps)
  if (scopedExists(deps, accountId) && (await databaseExists(factory, scopedStoreName(BASE, accountId)))) return 'scoped'
  const raw = await readStoreRaw(factory, BASE)
  return raw && raw.owner?.accountId === accountId ? 'legacy' : null
}

/**
 * Open the store an account ALREADY owns, for a boot with no sign-in (the offline cold start whose
 * MSAL cache could not be restored): its scoped store if it has a marker, else the legacy store if the
 * stamp names it. Anything else is `null`. It never adopts, never stamps, never creates, never marks:
 * it only re-opens what the device already knows is that account's, and nothing can sync from it.
 */
export async function resolveOwnedStore(accountId: string, deps: ResolveDeps = {}): Promise<ResolvedStore | null> {
  try {
    const owned = await findOwnedStore(accountId, deps)
    if (owned === 'scoped') {
      const name = scopedStoreName(BASE, accountId)
      const opened = await openStore(name, deps)
      const decision: StoreDecision = { kind: 'open-scoped', name }
      return { scope: scopeFromDecision(accountId, decision), decision, held: false, ...opened }
    }
    if (owned === 'legacy') {
      const opened = await openStore(BASE, deps)
      const decision: StoreDecision = { kind: 'open-legacy', name: BASE }
      return { scope: scopeFromDecision(accountId, decision), decision, held: false, ...opened }
    }
    return null
  } catch (e) {
    throw new StoreUnavailableError(e)
  }
}

// ------------------------------------------------------------------------------ the owner check

/**
 * Throws `ScopeMismatchError` unless the store still belongs to its scope's account. The stamp is
 * read fresh each time, so a store re-stamped under a running tab is noticed. A legacy store with NO
 * owner at all is refused too: legacy data is never sent on a guess. Run before every OneDrive
 * request (`onedrive.setRequestGuard`), so before every queued upload and before every queued DELETE:
 * the queue drains only under its owner.
 */
export async function assertStoreOwner(db: QuilDatabase, scope: StoreScope): Promise<void> {
  const stamp = await readOwner(db)
  if (stamp ? stamp.accountId !== scope.accountId : scope.legacy) {
    throw new ScopeMismatchError(scope, stamp?.accountId ?? null)
  }
}
